import { useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, Space, App, Modal, Form, Input, Checkbox, Spin } from 'antd';
import { Plus, Save, Trash2 } from 'lucide-react';
import { rolesService, type Permission, type RoleWithPermissions } from '@/services/rolesService';

const ROLE_COLORS: Record<string, string> = {
  SYS_ADMIN: 'red', BOD: 'purple', DEPT_HEAD: 'cyan', STAFF: 'default', WATCHER: 'default',
};

export function RolesMatrixTab() {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  // In-memory edit: roleId → Set<permissionId>
  const [matrix, setMatrix] = useState<Record<string, Set<string>>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [roleModal, setRoleModal] = useState<{ mode: 'create' | 'edit'; role?: RoleWithPermissions } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [perms, rolesData] = await Promise.all([
        rolesService.listPermissions(),
        rolesService.getMatrix(),
      ]);
      setPermissions(perms);
      setRoles(rolesData);
      const m: Record<string, Set<string>> = {};
      rolesData.forEach(r => m[r.id] = new Set(r.permissionIds));
      setMatrix(m);
      setDirty(new Set());
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const permByModule = useMemo(() => {
    const groups: Record<string, Permission[]> = {};
    permissions.forEach(p => {
      (groups[p.module] ||= []).push(p);
    });
    return groups;
  }, [permissions]);

  const toggle = (roleId: string, permId: string) => {
    setMatrix(m => {
      const copy = { ...m };
      const set = new Set(copy[roleId] || []);
      if (set.has(permId)) set.delete(permId);
      else set.add(permId);
      copy[roleId] = set;
      return copy;
    });
    setDirty(d => new Set(d).add(roleId));
  };

  const saveAll = async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    try {
      for (const roleId of dirty) {
        await rolesService.updatePermissions(roleId, Array.from(matrix[roleId] || []));
      }
      message.success(`Đã lưu ${dirty.size} vai trò`);
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  const handleDeleteRole = (r: RoleWithPermissions) => {
    modal.confirm({
      title: `Xóa vai trò ${r.code}?`,
      okText: 'Xóa', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await rolesService.deleteRole(r.id);
          message.success('Đã xóa');
          load();
        } catch (e: any) {
          message.error(e.response?.data?.message ?? e.message);
        }
      },
    });
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Vai trò & Ma trận phân quyền</div>
          <div style={{ fontSize: 11, color: '#7d8a98' }}>
            {roles.length} vai trò · {permissions.length} quyền · Tích ô để gán quyền cho vai trò
          </div>
        </div>
        <Space>
          <Button icon={<Plus size={14} />} onClick={() => setRoleModal({ mode: 'create' })}>Thêm vai trò</Button>
          <Button type="primary" icon={<Save size={14} />}
            disabled={dirty.size === 0} loading={saving} onClick={saveAll}>
            Lưu ({dirty.size})
          </Button>
        </Space>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #eceef1', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f6f7f8' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eceef1', position: 'sticky', left: 0, background: '#f6f7f8', zIndex: 2, minWidth: 240 }}>
                Quyền
              </th>
              {roles.map(r => (
                <th key={r.id} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '1px solid #eceef1', minWidth: 110 }}>
                  <div>
                    <Tag color={ROLE_COLORS[r.code] || 'default'} style={{ fontSize: 10 }}>{r.code}</Tag>
                    {dirty.has(r.id) && <span style={{ color: '#d97706', fontSize: 10 }}>●</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#7d8a98', marginTop: 2 }}>
                    {r.userCount} user
                  </div>
                  {!r.isSystem && (
                    <Button type="text" size="small" danger icon={<Trash2 size={10} />}
                      onClick={() => handleDeleteRole(r)} style={{ padding: 0, height: 18 }} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(permByModule).map(([module, perms]) => (
              <>
                <tr key={`m-${module}`}>
                  <td colSpan={roles.length + 1}
                    style={{ padding: '6px 12px', background: '#fafbfc', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #eceef1' }}>
                    Module: {module}
                  </td>
                </tr>
                {perms.map(p => (
                  <tr key={p.id}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eceef1', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                      <div className="mono" style={{ fontSize: 11, fontWeight: 500 }}>{p.code}</div>
                      <div style={{ fontSize: 10, color: '#7d8a98' }}>{p.name}</div>
                    </td>
                    {roles.map(r => {
                      const checked = matrix[r.id]?.has(p.id) || false;
                      return (
                        <td key={r.id}
                          style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #eceef1',
                                   background: checked ? '#e8f5f3' : undefined, cursor: 'pointer' }}
                          onClick={() => toggle(r.id, p.id)}>
                          <Checkbox checked={checked} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {roleModal && (
        <RoleFormModal mode={roleModal.mode} role={roleModal.role}
          onClose={() => setRoleModal(null)} onSuccess={() => { setRoleModal(null); load(); }} />
      )}
    </div>
  );
}

function RoleFormModal({ mode, role, onClose, onSuccess }: {
  mode: 'create' | 'edit'; role?: RoleWithPermissions; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && role) {
      form.setFieldsValue({ code: role.code, name: role.name, description: role.description });
    }
  }, [mode, role, form]);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (mode === 'create') {
        await rolesService.createRole(values);
        message.success('Đã thêm vai trò');
      } else if (role) {
        await rolesService.updateRole(role.id, { name: values.name, description: values.description });
        message.success('Đã cập nhật');
      }
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title={mode === 'create' ? 'Thêm vai trò' : `Sửa: ${role?.code}`}
      open onCancel={onClose} onOk={submit} confirmLoading={saving} okText="Lưu" cancelText="Hủy">
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="code" label="Mã vai trò" rules={[{ required: true }]}>
          <Input disabled={mode === 'edit'} placeholder="CUSTOM_ROLE" />
        </Form.Item>
        <Form.Item name="name" label="Tên hiển thị" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
